<script setup lang="ts">
  import * as Sentry from '@sentry/vue';
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue';
  import { router } from '@inertiajs/vue3';
  import axios from 'axios';

  import { PyneProvider, CelloProvider, SessionExpiredModal } from '~/components';
  import { useAccountStore } from '~/stores';
  import { launchIntegrations } from '~/integrations';
  import { useLocalization } from '~/utils/localization';
  import { useIntercom } from '~/composables';
  import { provideJune } from '~/composables/useJune';
  import { useEcho } from '~/utils/echo';

  const accountStore = useAccountStore();
  const { getAccountUsage } = accountStore;
  const {
    subscriptionType,
    subscriptionPlan,
    impersonator,
    intercomUserHash,
    mau: mauRef,
    account: accountRef,
  } = storeToRefs(accountStore);

  const mau = computed(() => mauRef.value!);
  const account = computed(() => accountRef.value!);

  const isProduction = computed<boolean>(() => window.location.origin === 'https://app.fynk.com');
  const isImpersonator = computed<boolean>(() => !!impersonator.value);

  const showSessionExpiredModal = ref(false);
  const Echo = useEcho();

  Echo.private(
    computed(() => `users.${mau.value.user.uuid}`),
    (channel) => {
      if (!channel) return;
      channel
        .listen('Pusher\\User\\LoggedOut', () => {
          location.reload();
        })
        .listen('Pusher\\User\\RoleChanged', () => {
          location.reload();
        })
        .listen('Pusher\\User\\ImpersonationStarted', () => {
          window.location.href = '/dashboard';
        });

      if (impersonator.value) {
        channel.listen('Pusher\\User\\ImpersonationStopped', () => {
          window.location.href = '/dashboard';
        });
      }
    }
  );

  Echo.private(
    computed(() => `accounts.${account.value?.uuid}`),
    (channel) => {
      if (!channel) return;
      channel
        .listen('Pusher\\Account\\SubscriptionUpdated', () => {
          router.reload({
            only: ['currentSubscription'],
          });
        })
        .listen('Pusher\\Account\\SubscriptionCreated', () => {
          router.reload({
            only: ['currentSubscription'],
          });
        });
    }
  );

  onBeforeMount(() => {
    setUpSentry();

    // Set up axios interceptors
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 || error.response?.status === 419) {
          showSessionExpiredModal.value = true;
          // return hanging promise, so no further calls will be done
          // and error won't be logged
          return new Promise(() => {});
        }
        return Promise.reject(error);
      }
    );

    if (isProduction.value && !isImpersonator.value && !mau.value.email?.endsWith('@fynk.com')) {
      launchIntegrations();
    }

    useIntercom({
      mau: mau.value,
      intercomUserHash: intercomUserHash.value,
      account: account.value || null,
      userLang: userLocaleWithoutRegion.value,
      subscriptionType: subscriptionType.value || undefined,
      subscriptionPlan: subscriptionPlan.value,
    });

    getAccountUsage();
  });

  onBeforeUnmount(() => {
    axios.interceptors.response.clear();
  });

  const setUpSentry = () => {
    Sentry.setUser({
      user_id: mau.value.user.uuid,
      user_email: mau.value.user.email,
      account_user_id: mau.value.uuid,
      account_id: account.value?.uuid || mau.value.account_uuid,
    });
  };

  provideJune();

  const { userLocaleWithoutRegion } = useLocalization();
</script>

<template>
  <slot />

  <PyneProvider
    :mau="mau"
    :account="account"
    :user-lang="userLocaleWithoutRegion"
    :subscription-type="subscriptionType || undefined"
    :subscription-plan="subscriptionPlan"
  />

  <CelloProvider
    :mau="mau"
    :account="account"
    :user-lang="userLocaleWithoutRegion"
  />

  <SessionExpiredModal :show="showSessionExpiredModal" />
</template>
